import React, { useEffect, useState } from "react";
import { fetchSiteLock, submitSiteUnlock } from "../lib/api";
import { useApp } from "../context/AppContext";
import { useAuth } from "../context/AuthContext";
import { Logo } from "./Logo";

const STORAGE_KEY = "kult_site_unlocked";

function resolveImg(u) {
  if (!u) return u;
  if (u.startsWith("http")) return u;
  if (u.startsWith("/api/")) return `${process.env.REACT_APP_BACKEND_URL}${u}`;
  return u;
}

export const SiteLockGate = ({ children }) => {
  const { lang } = useApp();
  const { user } = useAuth();
  const [lock, setLock] = useState(null); // {locked, image, title, message, has_password}
  const [ready, setReady] = useState(false);
  const [password, setPassword] = useState("");
  const [unlocked, setUnlocked] = useState(
    typeof window !== "undefined" && sessionStorage.getItem(STORAGE_KEY) === "1"
  );
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchSiteLock()
      .then((d) => setLock(d))
      .catch(() => setLock({ locked: false }))
      .finally(() => setReady(true));
  }, []);

  // Admins and artists never see the lock screen
  const adminBypass = user && (user.role === "admin" || user.role === "artist");
  // Allow access to the portal/login route even when locked, so admins can sign in
  const onLoginRoute =
    typeof window !== "undefined" &&
    (window.location.pathname.startsWith("/portal/login") ||
      window.location.pathname.startsWith("/portal/admin") ||
      window.location.pathname.startsWith("/portal/artist"));

  if (!ready) return null;
  if (!lock?.locked || adminBypass || unlocked || onLoginRoute) {
    return children;
  }

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await submitSiteUnlock(password);
      sessionStorage.setItem(STORAGE_KEY, "1");
      setUnlocked(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Wrong password");
    } finally {
      setSubmitting(false);
    }
  };

  const title = (lang === "pt" ? lock.title_pt : lock.title_en) || lock.title_en || "COMING SOON.";
  const msg =
    (lang === "pt" ? lock.message_pt : lock.message_en) ||
    lock.message_en ||
    "The cult is preparing. Enter the password to continue.";

  return (
    <div
      className="min-h-screen w-full bg-kult-black text-white relative overflow-hidden"
      data-testid="site-lock-gate"
    >
      {lock.image && (
        <img
          src={resolveImg(lock.image)}
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-40 grayscale"
        />
      )}
      <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/70 to-black" />
      <div className="relative z-10 min-h-screen flex flex-col items-center justify-center px-6 py-12 text-center">
        <div className="mb-10">
          <Logo size={42} />
        </div>
        <p className="font-mono text-[10px] tracking-[0.4em] text-kult-yellow mb-3">
          / {lock.has_password ? "PRIVATE ACCESS" : "COMING SOON"}
        </p>
        <h1 className="font-display text-5xl md:text-7xl leading-none max-w-3xl">
          {title}
        </h1>
        <p className="text-kult-muted mt-5 max-w-xl text-base md:text-lg">{msg}</p>

        {lock.has_password && (
          <form
            onSubmit={submit}
            className="mt-10 flex flex-col sm:flex-row items-stretch gap-3 w-full max-w-md"
            data-testid="site-lock-form"
          >
            <input
              type="password"
              required
              autoFocus
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="flex-1 bg-transparent border-2 border-kult-border focus:border-kult-pink outline-none px-4 py-3 font-mono text-sm text-center sm:text-left"
              data-testid="site-lock-password"
            />
            <button
              type="submit"
              disabled={submitting}
              className="bg-kult-pink text-black font-display text-xl px-6 py-3 hover:bg-kult-yellow transition-colors disabled:opacity-50"
              data-testid="site-lock-submit"
            >
              {submitting ? "..." : "ENTER ⚡"}
            </button>
          </form>
        )}
        {error && (
          <p className="mt-3 font-mono text-xs text-kult-pink" data-testid="site-lock-error">
            {error}
          </p>
        )}

        <p className="mt-12 font-mono text-[10px] tracking-[0.4em] text-kult-muted">
          KULTOPIA APPAREL · WRESTLING / MUSIC / EVENTS
        </p>
      </div>
    </div>
  );
};
